import { useState, useMemo } from 'react';
import {
  Search,
  ChevronRight,
  Store,
  Scissors,
  Sparkles,
  Palette,
  PenTool,
  Zap,
  Droplet,
  Gem,
  Heart,
  Activity,
  PartyPopper,
  Laptop,
  PawPrint,
  Car,
  Hammer,
  Briefcase,
  Camera,
  GraduationCap,
  MoreHorizontal,
  Loader2,
  type LucideIcon,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useBuscarEmpresas } from '@/components/hooks/useBuscarEmpresas';
import { useCategoriasEmpresa } from '@/components/hooks/useCategoriasEmpresa';
import { useSubcategorias } from '@/components/hooks/useSubcategorias';
import LoginDialog from '@/components/pages/dialogs/LoginDialog';
import ClientRegisterChoiceDialog from '@/components/pages/dialogs/ClientRegisterChoiceDialog';
import CompanyRegisterChoiceDialog from '@/components/pages/dialogs/CompanyRegisterChoiceDialog';
import LocationDialog from '@/components/pages/shared/LocationDialog';
import RecommendedCompanies from '@/components/pages/RecommendedCompanies';

// ── Design tokens ──────────────────────────────────────────────
// TEAL y CORAL se mantienen igual en ambos modos (acentos de marca).
// NAVY solo se usa ya en botones/CTAs con fondo propio; el resto de
// textos/iconos pasa a clases Tailwind con variante dark: para poder
// sobrescribirlos (un style inline con color fijo no se puede "apagar"
// en dark mode).
const TEAL = '#178F7C';
const CORAL = '#FF6B4A';

const CATEGORY_ICON: Record<string, LucideIcon> = {
  'Belleza': Sparkles,
  'Salud y Bienestar': Heart,
  'Deporte y Fitness': Activity,
  'Eventos y Ocio': PartyPopper,
  'Tecnología y Desarrollo Digital': Laptop,
  'Mascotas': PawPrint,
  'Automoción': Car,
  'Hogar y Reformas': Hammer,
  'Servicios Profesionales': Briefcase,
  'Fotografía y Audiovisual': Camera,
  'Formación': GraduationCap,
  'Otros': MoreHorizontal,
};

// Best-effort icon per subcategoria label, matched by keyword — usado
// como fallback para las subcategorías que vienen de Supabase, que no
// traen icono propio.
const SUBCATEGORY_ICON_RULES: Array<[RegExp, LucideIcon]> = [
  [/depila|láser|laser/i, Zap],
  [/micropigment/i, PenTool],
  [/maquilla/i, Palette],
  [/barber/i, Scissors],
  [/peluquer|corte|mecha|balayage|tinte/i, Scissors],
  [/facial|limpieza|hidrata/i, Droplet],
  [/manicur|pedicur|uñas|nail/i, Gem],
  [/masaje|relaj/i, Heart],
  [/fisio|osteopat|columna|cervical/i, Activity],
  [/estetic/i, Sparkles],
];

const getSubcategoryIcon = (label: string): LucideIcon => {
  const rule = SUBCATEGORY_ICON_RULES.find(([re]) => re.test(label));
  return rule ? rule[1] : Sparkles;
};

const MobileLoginScreen = () => {
  const [loginOpen, setLoginOpen] = useState(false);
  const [clientRegisterOpen, setClientRegisterOpen] = useState(false);
  const [companyRegisterOpen, setCompanyRegisterOpen] = useState(false);
  const [selectedCategoriaId, setSelectedCategoriaId] = useState<string | null>(null);

  const {
    searchTerm,
    setSearchTerm,
    empresas,
    showLocationDialog,
    handleAllowLocation,
    handleRejectLocation,
  } = useBuscarEmpresas();

  // Catálogo de categorías cargado dinámicamente desde Supabase
  // (antes vivía hardcodeado en el frontend y se desincronizaba entre
  // entornos — ver vista_cb_categoria_empresa).
  const { categorias, loadingCategorias } = useCategoriasEmpresa();

  const { subcategorias: subcategoriasCategoria, loading: loadingSubcategorias } =
    useSubcategorias(selectedCategoriaId);

  // Solo mostramos categorías que realmente tienen empresas activas.
  const categoriasDisponibles = useMemo(() => {
    const presentes = new Set(empresas.map((e) => e.categoria).filter(Boolean));
    return categorias.filter((c) => presentes.has(c.categoria));
  }, [empresas, categorias]);

  const toggleCategoria = (categoria: { id_categoria: string; categoria: string }) => {
    const isActive = selectedCategoriaId === categoria.id_categoria;
    setSelectedCategoriaId(isActive ? null : categoria.id_categoria);
    setSearchTerm(isActive ? '' : categoria.categoria);
  };

  // Buscador único: nombre, servicios, subcategorías y ciudad a la vez,
  // así el usuario no necesita un segundo campo para localización.
  const searchResults = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return empresas;
    return empresas.filter((e) => {
      const haystack = [
        e.empresa,
        e.categoria,
        e.ciudad,
        e.ciudad_slug?.replace(/-/g, ' '),
        ...(e.subcategorias ?? []),
        ...(e.servicios ?? []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [empresas, searchTerm]);

  return (
    <div className="min-h-screen flex flex-col bg-[#F6F8F7] dark:bg-black">
      {/* ── HERO — soft onboarding-style cover, con variante dark ── */}
      <div className="relative pt-5 pb-7 px-5 overflow-hidden bg-gradient-to-b from-white via-[#F3F8F6] to-[#E7F3EF] dark:from-[#1c1c1e] dark:via-[#1c1c1e] dark:to-black">
        <span
          aria-hidden
          className="absolute -top-10 -right-14 w-40 h-40 rounded-full blur-2xl"
          style={{ background: TEAL, opacity: 0.14 }}
        />
        <span
          aria-hidden
          className="absolute top-24 -left-16 w-32 h-32 rounded-full blur-2xl"
          style={{ background: '#C9A15A', opacity: 0.12 }}
        />

        {/* slim top bar: logo + compact auth links */}
        <div className="relative flex items-center justify-between mb-7">
          <img
            src="https://ijolbckmgiwomizpzrae.supabase.co/storage/v1/object/public/bookoll/logos/logobookoll.png"
            alt="Bookoll"
            className="h-6"
          />
          <div className="flex items-center gap-1">
            <LoginDialog
              open={loginOpen}
              setOpen={setLoginOpen}
              context="login"
              onRegisterClient={() =>
                setTimeout(() => setClientRegisterOpen(true), 220)
              }
            >
              <button
                onClick={() => setLoginOpen(true)}
                className="text-[12.5px] font-semibold px-3 py-1.5 rounded-full active:scale-95 transition-transform text-[#0F2A47] dark:text-white"
              >
                Iniciar sesión
              </button>
            </LoginDialog>
            <button
              onClick={() => setClientRegisterOpen(true)}
              className="text-[12.5px] font-semibold text-white px-3.5 py-1.5 rounded-full active:scale-95 transition-transform bg-[#0F2A47] dark:bg-white/10 dark:border dark:border-white/10"
            >
              Crear cuenta
            </button>
          </div>
        </div>

        <p className="relative text-[11px] font-semibold tracking-[0.16em] uppercase mb-2 text-[#178F7C] dark:text-teal-400">
          Hola de nuevo
        </p>
        <h1 className="relative text-[26px] font-bold leading-[1.2] mb-1.5 max-w-[16ch] text-[#0F2A47] dark:text-white">
          Encuentra tu cita perfecta
        </h1>
        <p className="relative text-[13.5px] mb-5 text-[#0F2A47]/60 dark:text-white/60">
          Los mejores profesionales de tu ciudad
        </p>

        {/* buscador único: empresa, servicio o ciudad */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#178F7C] z-10" />
          <Input
            placeholder="Empresa, servicio o ciudad..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 h-12 rounded-2xl border-0 shadow-lg shadow-black/10 dark:shadow-black/40 bg-white dark:bg-[#1c1c1e] dark:text-white dark:border dark:border-[#178F7C]/60 text-[15px]"
          />
        </div>

        {/* ── ACCESOS RÁPIDOS POR CATEGORÍA ── */}
        {loadingCategorias ? (
          <div className="relative mt-5 flex items-center gap-2 text-[12.5px] text-[#0F2A47]/50 dark:text-white/50">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Cargando categorías...
          </div>
        ) : (
          categoriasDisponibles.length > 0 && (
            <div className="relative mt-5 -mx-5 px-5">
              <div className="flex gap-4 overflow-x-auto no-scrollbar">
                {categoriasDisponibles.map((cat) => {
                  const Icon = CATEGORY_ICON[cat.categoria] ?? Sparkles;
                  const active = selectedCategoriaId === cat.id_categoria;
                  return (
                    <button
                      key={cat.id_categoria}
                      onClick={() => toggleCategoria(cat)}
                      className="shrink-0 flex flex-col items-center gap-1.5 w-16 active:scale-95 transition-transform"
                    >
                      <span
                        className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                          active
                            ? 'bg-[#0F2A47] dark:bg-white/15'
                            : 'bg-white dark:bg-[#1c1c1e] shadow-[0_1px_3px_rgba(15,42,71,0.1)] dark:shadow-none dark:border dark:border-white/10'
                        }`}
                      >
                        <Icon
                          className="w-5 h-5"
                          style={{ color: active ? '#fff' : TEAL }}
                        />
                      </span>
                      <span
                        className={`text-[10.5px] font-medium leading-tight text-center truncate w-full ${
                          active
                            ? 'text-[#0F2A47] dark:text-white'
                            : 'text-[#0F2A47]/55 dark:text-white/55'
                        }`}
                      >
                        {cat.categoria}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* subcategorías de la categoría seleccionada */}
              {selectedCategoriaId && (
                <div className="mt-3 pt-3 border-t border-black/5 dark:border-white/10">
                  {loadingSubcategorias ? (
                    <div className="flex items-center gap-2 text-[12.5px] py-1 text-[#0F2A47]/50 dark:text-white/50">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Cargando subcategorías...
                    </div>
                  ) : subcategoriasCategoria.length > 0 ? (
                    <div className="flex gap-4 overflow-x-auto no-scrollbar">
                      {subcategoriasCategoria.map((sub) => {
                        const Icon = getSubcategoryIcon(sub.subcategoria);
                        const active = searchTerm === sub.subcategoria;
                        return (
                          <button
                            key={sub.id_subcategoria}
                            onClick={() => setSearchTerm(active ? '' : sub.subcategoria)}
                            className="shrink-0 flex flex-col items-center gap-1.5 w-16 active:scale-95 transition-transform"
                          >
                            <span
                              className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                                active
                                  ? 'bg-[#0F2A47] dark:bg-white/15'
                                  : 'bg-white dark:bg-[#1c1c1e] shadow-[0_1px_3px_rgba(15,42,71,0.1)] dark:shadow-none dark:border dark:border-white/10'
                              }`}
                            >
                              <Icon className="w-5 h-5" style={{ color: active ? '#fff' : TEAL }} />
                            </span>
                            <span
                              className={`text-[10.5px] font-medium leading-tight text-center truncate w-full ${
                                active
                                  ? 'text-[#0F2A47] dark:text-white'
                                  : 'text-[#0F2A47]/55 dark:text-white/55'
                              }`}
                            >
                              {sub.subcategoria}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-[12.5px] text-[#0F2A47]/40 dark:text-white/40">
                      Sin subcategorías para esta categoría.
                    </p>
                  )}
                </div>
              )}
            </div>
          )
        )}
      </div>

      {/* ── RESULTADOS / RECOMENDADOS ── */}
      <RecommendedCompanies empresas={searchResults} />

      {/* ── CTA NEGOCIO ── */}
      <div className="px-5 pb-8 mt-auto">
        <button
          onClick={() => setCompanyRegisterOpen(true)}
          className="relative w-full flex items-center gap-3 rounded-2xl p-4 text-left active:scale-[0.98] transition-transform overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${TEAL} 0%, #10665A 100%)` }}
        >
          <span
            aria-hidden
            className="absolute -right-3 -top-3 w-8 h-8 rounded-full"
            style={{ background: CORAL, opacity: 0.35 }}
          />
          <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
            <Store className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold text-sm">¿Tienes un negocio?</p>
            <p className="text-white/70 text-xs">Regístralo gratis y recibe reservas online</p>
          </div>
          <ChevronRight className="w-4 h-4 text-white/70 shrink-0" />
        </button>
      </div>

      <ClientRegisterChoiceDialog open={clientRegisterOpen} onOpenChange={setClientRegisterOpen} />
      <CompanyRegisterChoiceDialog open={companyRegisterOpen} onOpenChange={setCompanyRegisterOpen} />
      <LocationDialog open={showLocationDialog} onAllow={handleAllowLocation} onReject={handleRejectLocation} />
    </div>
  );
};

export default MobileLoginScreen;